import { createPortal } from 'react-dom'
import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  getNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  type NotificationApi,
} from '@/services/api/notifications'

interface NotificationModalProps {
  isOpen: boolean
  onClose: () => void
  onMarkedRead?: () => void
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)
  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins} min ago`
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`
  if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`
  return date.toLocaleDateString()
}

const NotificationModal = ({ isOpen, onClose, onMarkedRead }: NotificationModalProps) => {
  const navigate = useNavigate()
  const [notifications, setNotifications] = useState<NotificationApi[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [filterUnread, setFilterUnread] = useState<boolean>(false)

  const fetchNotifications = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await getNotifications({ page: 1, limit: 50, unreadOnly: filterUnread })
      setNotifications(res.notifications)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load notifications')
      setNotifications([])
    } finally {
      setLoading(false)
    }
  }, [filterUnread])

  useEffect(() => {
    if (isOpen) {
      fetchNotifications()
    }
  }, [isOpen, fetchNotifications])

  const handleItemClick = async (n: NotificationApi) => {
    if (!n.read) {
      try {
        await markNotificationRead(n.id)
        setNotifications((prev) =>
          prev.map((x) => (x.id === n.id ? { ...x, read: true } : x))
        )
        onMarkedRead?.()
      } catch {
        // ignore
      }
    }
    if (n.link) {
      onClose()
      navigate(n.link)
    }
  }

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead()
      setNotifications((prev) => prev.map((x) => ({ ...x, read: true })))
      onMarkedRead?.()
    } catch {
      // ignore
    }
  }

  if (!isOpen) return null

  const modalContent = (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-end"
      onClick={onClose}
      style={{ zIndex: 60 }}
    >
      <div
        className="bg-white h-full w-full max-w-md shadow-xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        style={{
          fontFamily: 'Lexend Deca',
          zIndex: 70,
          borderTopLeftRadius: '12px',
        }}
      >
        <div
          className="flex items-center justify-between border-b border-gray-200"
          style={{
            width: '100%',
            height: '74px',
            backgroundColor: '#F2F4F2',
            borderTopLeftRadius: '12px',
            paddingLeft: '24px',
            paddingRight: '24px',
          }}
        >
          <h2
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 600,
              fontStyle: 'normal',
              fontSize: '18px',
              lineHeight: '26px',
              letterSpacing: '0px',
              color: '#212126',
            }}
          >
            Notifications
          </h2>
          <button
            onClick={onClose}
            className="flex items-center justify-center hover:opacity-80 transition-opacity"
            style={{ width: '24px', height: '24px' }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="12" cy="12" r="10.5" stroke="#212126" strokeWidth="1.5" fill="none" />
              <line x1="9.17" y1="9.17" x2="14.83" y2="14.83" stroke="#212126" strokeWidth="1.5" strokeLinecap="round" />
              <line x1="14.83" y1="9.17" x2="9.17" y2="14.83" stroke="#212126" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="p-4 border-b border-gray-200 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setFilterUnread(false)}
              className="px-4 py-2 rounded-md transition-colors"
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: !filterUnread ? 600 : 400,
                fontSize: '14px',
                color: !filterUnread ? '#FFFFFF' : '#69697A',
                backgroundColor: !filterUnread ? '#212126' : 'transparent',
              }}
            >
              All
            </button>
            <button
              onClick={() => setFilterUnread(true)}
              className="px-4 py-2 rounded-md transition-colors"
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: filterUnread ? 600 : 400,
                fontSize: '14px',
                color: filterUnread ? '#FFFFFF' : '#69697A',
                backgroundColor: filterUnread ? '#212126' : 'transparent',
              }}
            >
              Unread
            </button>
          </div>
          {notifications.some((n) => !n.read) && (
            <button
              onClick={handleMarkAllRead}
              className="text-sm"
              style={{
                fontFamily: 'Lexend Deca',
                color: '#212126',
                textDecoration: 'underline',
              }}
            >
              Mark all read
            </button>
          )}
        </div>

        <div className="divide-y divide-gray-100">
          {loading && (
            <div className="p-6 text-center" style={{ fontFamily: 'Lexend Deca', color: '#69697A' }}>
              Loading...
            </div>
          )}
          {error && (
            <div className="p-6 text-center" style={{ fontFamily: 'Lexend Deca', color: '#E93323' }}>
              {error}
            </div>
          )}
          {!loading && !error && notifications.length === 0 && (
            <div className="p-6 text-center" style={{ fontFamily: 'Lexend Deca', color: '#69697A' }}>
              No notifications
            </div>
          )}
          {!loading &&
            !error &&
            notifications.map((notification) => (
              <div
                key={notification.id}
                onClick={() => handleItemClick(notification)}
                className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <h3
                      className="font-semibold mb-1"
                      style={{
                        fontFamily: 'Lexend Deca',
                        fontWeight: 600,
                        color: '#212126',
                        fontSize: '14px',
                      }}
                    >
                      {notification.title}
                    </h3>
                    <p
                      className="text-sm"
                      style={{
                        fontFamily: 'Lexend Deca',
                        color: '#69697A',
                        fontSize: '12px',
                        lineHeight: '20px',
                      }}
                    >
                      {notification.message}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {!notification.read && (
                      <div
                        className="rounded-full"
                        style={{
                          width: '8px',
                          height: '8px',
                          backgroundColor: '#13AB5B',
                        }}
                      />
                    )}
                    <span
                      className="text-xs whitespace-nowrap"
                      style={{
                        fontFamily: 'Lexend Deca',
                        color: '#69697A',
                        fontSize: '12px',
                      }}
                    >
                      {formatRelativeTime(notification.createdAt)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  )

  return createPortal(modalContent, document.body)
}

export default NotificationModal
